import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel as MuiFormControlLabel,
} from '@mui/material';
import { Fade } from '@mui/material';
import {
  AccessTime,
  TaskAlt,
  Close,
  Warning,
  Report,
  InfoOutlined,
  Celebration,
  LocalPolice,
  AssistantPhoto,
  FavoriteBorder,
  AutoStories,
  Build,
} from '@mui/icons-material';
import Info from './Info';
import { useAdminLanguage } from './AdminI18n';

const iconComponents = {
  accesstime: AccessTime,
  taskalt: TaskAlt,
  close: Close,
  warning: Warning,
  report: Report,
  infoutline: InfoOutlined,
  celebration: Celebration,
  localpolice: LocalPolice,
  assistantphoto: AssistantPhoto,
  favoriteborder: FavoriteBorder,
  autostories: AutoStories,
  build: Build,
};

const iconLabels = {
  accesstime: 'Temps et horaires',
  taskalt: 'Tâche accomplie',
  close: 'Fermer ou refus',
  warning: 'Attention',
  report: 'Rapport',
  infoutline: 'Information',
  celebration: 'Célébration',
  localpolice: 'Police',
  assistantphoto: 'Alerte',
  favoriteborder: 'Appréciation',
  autostories: 'Renseignements',
  build: 'Maintenance',
};

const AlertAdmin = () => {
  const { t } = useAdminLanguage();
  const [alert, setAlert] = useState({});
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchAlert = async () => {
      try {
        const response = await fetch('/api/alert');
        if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);
        const data = await response.json();
        setAlert(data || {
          show_alert: false,
          alert_title: '',
          alert_content: '',
          alert_icon: 'infoutline'
        });
      } catch (err) {
        console.error('Erreur lors de la récupération des alertes:', err);
        setError('Une erreur est survenue lors du chargement des alertes.');
      } finally {
        setLoading(false);
      }
    };

    fetchAlert();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Validation de la longueur en temps réel
    if (name === 'alert_title' && value.length > 100) {
      return;
    }
    if (name === 'alert_content' && value.length > 500) {
      return;
    }
    
    setAlert((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (event) => {
    setAlert((prev) => ({ ...prev, show_alert: event.target.checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');
    setError(null);

    try {
      const response = await fetch('/api/alert', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(alert),
      });

      if (response.ok) {
        setMessage('Alerte mise à jour avec succès !');
      } else {
        const data = await response.json();
        setError(data.error || 'Erreur lors de la mise à jour de l\'alerte.');
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      setError('Erreur lors de la communication avec le serveur.');
    } finally {
      setSubmitting(false);
    }
  };

  const IconPreview = ({ iconName }) => {
    const IconComponent = iconComponents[iconName];
    return IconComponent ? <IconComponent sx={{ fontSize: 24 }} /> : null;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in={!loading} timeout={500}>
        <Box component="form" onSubmit={handleSubmit} autoComplete="off">
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Gestion des Alertes')}
          </Typography>

          <Info type="alert" />

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <MuiFormControlLabel
                  control={
                    <Switch
                      checked={alert.show_alert || false}
                      onChange={handleSwitchChange}
                      disabled={submitting}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {alert.show_alert ? t('Alerte activée') : t('Alerte désactivée')}
                    </Typography>
                  }
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {alert.show_alert 
                    ? t("L'alerte sera visible pour tous les utilisateurs") 
                    : t("L'alerte ne sera pas affichée aux utilisateurs")
                  }
                </Typography>
              </Box>

              <TextField
                fullWidth
                label={t("Titre de l'alerte")}
                name="alert_title"
                value={alert.alert_title || ''}
                onChange={handleChange}
                variant="outlined"
                margin="normal"
                disabled={submitting}
                inputProps={{ maxLength: 60 }}
                helperText={`${(alert.alert_title || '').length}/60 ${t('caractères') || 'caractères'}`}
              />

              <TextField
                fullWidth
                label={t("Contenu de l'alerte")}
                name="alert_content"
                value={alert.alert_content || ''}
                onChange={handleChange}
                variant="outlined"
                margin="normal"
                disabled={submitting}
                multiline
                rows={4}
                inputProps={{ maxLength: 300 }}
                helperText={`${(alert.alert_content || '').length}/300 ${t('caractères') || 'caractères'}`}
              />

              <FormControl component="fieldset" sx={{ my: 3 }}>
                <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'bold' }}>
                  {t("Icône de l'alerte")}
                </FormLabel>
                <Grid container spacing={2}>
                  {Object.entries(iconComponents).map(([iconKey, IconComponent]) => (
                    <Grid item xs={12} sm={6} md={4} key={iconKey}>
                      <Card 
                        sx={{ 
                          cursor: 'pointer',
                          border: alert.alert_icon === iconKey ? 2 : 1,
                          borderColor: alert.alert_icon === iconKey ? 'primary.main' : 'grey.300',
                          '&:hover': { borderColor: 'primary.main' },
                          transition: 'all 0.2s'
                        }}
                        onClick={() => handleChange({ target: { name: 'alert_icon', value: iconKey } })}
                      >
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <IconComponent sx={{ fontSize: 32, mb: 1, color: 'primary.main' }} />
                          <Typography variant="body2">
                            {t(iconLabels[iconKey])}
                          </Typography>
                          <Radio
                            checked={alert.alert_icon === iconKey}
                            value={iconKey}
                            name="alert_icon"
                            sx={{ mt: 1 }}
                            disabled={submitting}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </FormControl>

              {alert.alert_icon && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {t("Aperçu de l'alerte :")}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconPreview iconName={alert.alert_icon} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {alert.alert_title || t("Titre de l'alerte")}
                      </Typography>
                      <Typography variant="body2">
                        {alert.alert_content || t("Contenu de l'alerte")}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={submitting}
                startIcon={submitting && <CircularProgress size={20} />}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  textTransform: 'none',
                  transition: 'all 0.3s',
                  '&:hover': { boxShadow: 6 },
                  maxWidth: '300px',
                  mx: 'auto',
                  display: 'block',
                  mt: 3,
                }}
              >
                {submitting ? t('Enregistrement...') : t('Enregistrer')}
              </Button>

              {message && (
                <Alert severity="success" sx={{ mt: 2, maxWidth: '500px', mx: 'auto' }}>
                  {message}
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mt: 2, maxWidth: '500px', mx: 'auto' }}>
                  {error}
                </Alert>
              )}
            </>
          )}
        </Box>
      </Fade>
    </Container>
  );
};

export default AlertAdmin;
